/* -----------------Globals--------------- */
import { router, useNavigation, usePathname, useRouter } from "expo-router";

/* -----------------UI--------------- */
import { Appbar, type AppbarHeaderProps, Text } from "react-native-paper";

import { isWeb } from "@helpers/platform";
/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import useUserInfo from "@hooks/useUserInfo";
import { Box, type BreadcrumbType, CustomBreadcrumbWeb } from "@memorang/ui";
import AvatarGenerator from "@memorang/ui/src/components/AvatarGenerator";
import FeedbackDialogContainer from "containers/FeedbackDialogContainer";
import { useState } from "react";
import RowStats, { type Stat } from "../RowStats";
import { CommandBar } from "./CommandBar";

const TitleContent = ({
	headerTitle,
	stats,
	breadcrumbs,
	renderSearchbar,
}: {
	align?: "center" | "flex-start";
	headerTitle?: string;
	subtitle?: string | React.ReactNode;
	stats?: Stat[];
	breadcrumbs?: BreadcrumbType[];
	renderSearchbar?: () => React.ReactNode;
}) => {
	const { isMobile, isMediumWindowSize } = useDevice();
	return (
		<Box gap={isMobile || isMediumWindowSize ? 8 : 16} width={"100%"}>
			{renderSearchbar?.()}
			<Box gap={4} paddingTop={isMobile ? 0 : 16}>
				{breadcrumbs?.length && !isMobile && isWeb ? (
					<CustomBreadcrumbWeb breadcrumbs={breadcrumbs} />
				) : null}
				<Box>
					{headerTitle && (
						<Text
							numberOfLines={1}
							style={{ maxWidth: isMobile ? 400 : "100%" }}
							variant={isMobile ? "titleLarge" : "headlineMedium"}
						>
							{headerTitle as string}
						</Text>
					)}
					{/* {subtitle && <Text variant="bodyMedium">{subtitle}</Text>} */}
				</Box>
			</Box>
			{stats?.length && !isMobile ? (
				<RowStats stacked={false} stats={stats} />
			) : null}
		</Box>
	);
};

const CustomNavigationBar = (
	props: {
		back?: boolean;
		stats?: Stat[];
		breadcrumbs?: BreadcrumbType[];
		showHambugerMenu?: boolean;
		showCommandBar?: boolean;
		renderSearchbar?: () => React.ReactNode;
		align?: "center" | "flex-start";
		options: {
			headerTitle?: string;
			subtitle?: string | React.ReactNode;
			headerRight?: () => React.ReactNode;
			headerLeft?: () => React.ReactNode;
		};
	} & Omit<AppbarHeaderProps, "children">,
) => {
	const {
		back,
		stats,
		options: { headerTitle, subtitle, headerRight, headerLeft },
		breadcrumbs,
		renderSearchbar,
		showHambugerMenu,
		align,
		showCommandBar,
		...rest
	} = props;
	const theme = useAppTheme();

	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const _pathname = usePathname();

	const { handleFeedbackNavigation } = useCustomNavigation();
	const userInfo = useUserInfo();

	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};

	const { isMobile, isMediumWindowSize } = useDevice();

	// FOR SOME REASON, the medium prop medium does not work well on tablet
	const mode = isMobile ? "small" : isMediumWindowSize ? "large" : "medium";
	const finalAlign =
		align || (isMobile ? ("center" as const) : ("flex-start" as const));

	const renderHeaderRight = () => {
		if (headerRight) {
			return headerRight();
		}

		return (
			<Box flexDirection="row" alignItems="center">
				<Appbar.Action
					icon="comment-quote-outline"
					onPress={() => handleFeedbackNavigation(toggleShowFeedbackDialog)}
				/>

				<Appbar.Action
					onPress={() => router.push("/(protected)/account/settings")}
					icon={() => (
						<AvatarGenerator
							avatarType={"initials"}
							size={24}
							firstName={userInfo?.name?.split(" ")[0] || ""}
							lastName={userInfo?.name?.split(" ")[1] || ""}
							customStyle={{
								position: "relative",
								top: undefined,
							}}
						/>
					)}
				/>
			</Box>
		);
	};

	const router = useRouter();
	const navigation = useNavigation<{
		toggleDrawer: () => void;
	}>();

	if (isWeb && !isMobile) {
		return (
			<Box
				style={{
					height: 56,
					backgroundColor: theme.dark
						? theme.colors.surface
						: theme.colors.white,
					borderBottomWidth: 1,
					borderBottomColor:
						theme.colors.outlineVariant ||
						theme.colors.surfaceVariant ||
						"#eae7de",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
					paddingHorizontal: 16,
					width: "100%",
				}}
			>
				{/* Left Side: Back/Menu, Breadcrumbs, Page Title */}
				<Box flexDirection="row" alignItems="center" gap={12} flex={1}>
					{back && !headerLeft && (
						<Appbar.BackAction
							onPress={() => {
								if (router.canGoBack()) {
									router.back();
								} else {
									router.push("/(protected)/(tabs)/home");
								}
							}}
						/>
					)}
					{headerLeft?.()}
					<Box gap={2}>
						{breadcrumbs?.length ? (
							<CustomBreadcrumbWeb breadcrumbs={breadcrumbs} />
						) : null}
						{headerTitle && (
							<Text variant="titleMedium" numberOfLines={1}>
								{headerTitle as string}
							</Text>
						)}
					</Box>
					{stats?.length ? <RowStats stacked={false} stats={stats} /> : null}
				</Box>

				{/* Right Side: Search bar / Command bar & Trailing Actions */}
				<Box flexDirection="row" alignItems="center" gap={12}>
					{renderSearchbar?.()}
					{showCommandBar && <CommandBar />}
					{renderHeaderRight()}
				</Box>

				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			</Box>
		);
	}

	return (
		//@ts-ignore
		<Appbar.Header
			theme={theme}
			mode={mode}
			elevated
			{...rest}
			{...(isWeb
				? {
						style: {
							height: "fit-content",
						},
					}
				: {})}
		>
			{isWeb && (
				<Box
					alignItems="center"
					flexDirection="row"
					flex={isMobile ? undefined : 1}
				>
					{isMobile && showHambugerMenu && (
						<Appbar.Action
							icon="menu"
							onPress={() => navigation.toggleDrawer()}
						/>
					)}
					{showCommandBar && !isMobile && (
						<Box paddingLeft={12}>
							<CommandBar />
						</Box>
					)}
				</Box>
			)}

			{headerLeft?.()}
			{back && !headerLeft && (
				<Appbar.BackAction
					onPress={() => {
						if (router.canGoBack()) {
							router.back();
						} else {
							router.push("/(protected)/(tabs)/home");
						}
					}}
				/>
			)}
			<Appbar.Content
				title={
					<TitleContent
						align={finalAlign}
						headerTitle={headerTitle}
						subtitle={subtitle}
						stats={stats}
						breadcrumbs={breadcrumbs}
						renderSearchbar={renderSearchbar}
					/>
				}
			/>
			{renderHeaderRight()}
			{isWeb && (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</Appbar.Header>
	);
};

export default CustomNavigationBar;
